import { useEffect, useReducer } from 'react'

import { Box, Button, Chip as MUIChip, Theme, Typography } from '@material-ui/core'
import { makeStyles, styled } from '@material-ui/core/styles'
import { addMonths, endOfMonth, endOfWeek, format, isAfter, startOfMonth, startOfWeek } from 'date-fns'

import { Class, HistoricFollowerYear } from 'routes/highschools/ExploreTable/gradYears'
import { createRangeFilterChip } from 'routes/highschools/utils/filter-utils'

import getFilterCount from '../../filter-state-count'
import { TITLE_1_PROGRAM_OPTIONS } from '../../title-one'
import { LocaleOption, UIFilters, unknownLocation } from '../../types'
import Drawer, { Heading } from '../Drawer'
import FilterDemographics from './FilterDemographics'
import FilterLocations from './FilterLocations'
import FilterPostSecondary from './FilterPostSecondary'
import FilterSchoolDetails from './FilterSchoolDetails'
import { unknownLocationOption } from './FilterState'
import FilterStudentEngagement from './FilterStudentEngagement'
import FilterVisits from './FilterVisits'

type State = {
  formState: UIFilters
}

export type Action =
  | {
      type: 'update-form-state'
      formState: Partial<UIFilters>
    }
  | {
      type: 'replace-form-state'
      formState: UIFilters
    }
  | {
      type: 'reset-form-state'
    }
  | {
      type: 'select-visit-availability-option'
      option?: 'this-week' | 'this-month' | 'next-month' | 'custom'
    }
  | {
      type: 'update-visibility-dates'
      visitAvailabilityDates: {
        startDate?: Date | null
        endDate?: Date | null
      }
    }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'update-form-state': {
      return {
        ...state,
        formState: {
          ...state.formState,
          ...action.formState,
        },
      }
    }
    case 'replace-form-state': {
      return {
        ...state,
        formState: action.formState,
      }
    }
    case 'reset-form-state':
      return {
        ...state,
        formState: {},
      }
    case 'select-visit-availability-option':
      if (!action.option) {
        return {
          ...state,
          formState: {
            ...state.formState,
            visitRange: undefined,
          },
        }
      }

      return {
        ...state,
        formState: {
          ...state.formState,
          visitRange: getVisitVailabilityFromOption(action.option),
        },
      }
    case 'update-visibility-dates':
      return {
        ...state,
        formState: {
          ...state.formState,
          visitRange: {
            type: 'custom',
            ...state.formState.visitRange,
            startDate: dateOrFallback(action.visitAvailabilityDates.startDate, state.formState.visitRange?.startDate),
            endDate: dateOrFallback(action.visitAvailabilityDates.endDate, state.formState.visitRange?.endDate),
          },
        },
      }
    default:
      return state
  }
}

const SectionBox = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3),
  '&:last-child': {
    borderBottom: 'none',
  },
}))

export const parseNumeric = (v: string): number | undefined => {
  const n = parseInt(v, 10)
  return isNaN(n) ? undefined : n
}

const Chip = styled(MUIChip)(() => ({
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}))

type FilterChip = {
  key: keyof UIFilters
  label: string
}

const VIRTUAL_LABELS = {
  virtual: 'Virtual Only',
  inPerson: 'In Person Only',
}

const COLLEGE_FAIR_LABELS = {
  collegeFair: 'College Fair Only',
  highSchoolVisit: 'High School Visit Only',
}

type VisitRangeLabels = {
  type: {
    [K in NonNullable<UIFilters['visitRange']>['type']]: string
  }
  date: {
    from: string
    until: string
    range: string
  }
}

type SchoolTypeLabels = {
  [K in NonNullable<UIFilters['schoolType']>]: string
}

type RangeLabels = {
  label: string
  range: string
  min: string
  max: string
}

const FILTER_LABELS: {
  [TKey in keyof Omit<
    UIFilters,
    | 'virtual'
    | 'collegeFair'
    | 'text'
    | 'locations'
    | 'religiousAffiliation'
    | 'locale'
    | 'cities'
    | 'zipCodes'
    | 'geomarkets'
  >]-?: TKey extends 'visitRange'
    ? VisitRangeLabels
    : TKey extends 'schoolType'
      ? SchoolTypeLabels
      : TKey extends 'title1Program'
        ? typeof TITLE_1_PROGRAM_OPTIONS
        : TKey extends
              | 'pctFreeReducedLunch'
              | 'pctFirstGenerationStudents'
              | 'classSize'
              | 'interestedHandraisers'
              | 'interestedFollowers'
              | 'historicFollowers'
              | 'pctAttendFourYearCollegeStudents'
              | 'pctAttendTwoYearCollegeStudents'
              | 'pctWhiteCaucasianStudents'
              | 'pctBlackAfricanAmericanStudents'
              | 'pctHispanicLatinoStudents'
              | 'pctAsianStudents'
              | 'pctNativeAmericanStudents'
              | 'pctOtherUndisclosedStudents'
          ? RangeLabels
          : TKey extends 'isActive' | 'isTitle1Eligible' | 'hasCounselorSuggestions' | 'isTarget'
            ? string
            : never
} = {
  visitRange: {
    type: {
      'this-week': 'This Week',
      'this-month': 'This Month',
      'next-month': 'Next Month',
      custom: 'Custom Range',
    },
    date: {
      from: 'From',
      until: 'Until',
      range: '{start} - {end}',
    },
  },
  schoolType: {
    public: 'Public Schools',
    private: 'Private Schools',
  },
  title1Program: TITLE_1_PROGRAM_OPTIONS,
  pctFreeReducedLunch: {
    label: 'Free/Reduced Lunch',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctAttendTwoYearCollegeStudents: {
    label: 'Attended 2-Year',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctAttendFourYearCollegeStudents: {
    label: 'Attended 4-Year',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctFirstGenerationStudents: {
    label: 'First Generation',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  classSize: {
    label: 'Class Size',
    range: '{min} - {max}',
    min: '≥{value}',
    max: '≤{value}',
  },
  isActive: 'Only Scoir schools',
  isTarget: 'Only target schools',
  isTitle1Eligible: 'Title 1 Eligible Schools',
  hasCounselorSuggestions: 'Promoter Schools',
  interestedHandraisers: {
    label: 'Handraisers',
    range: '{min} - {max}',
    min: '≥{value}',
    max: '≤{value}',
  },
  interestedFollowers: {
    label: 'Followers',
    range: '{min} - {max}',
    min: '≥{value}',
    max: '≤{value}',
  },
  historicFollowers: {
    label: 'Historic Followers',
    range: '{min} - {max}',
    min: '≥{value}',
    max: '≤{value}',
  },
  pctWhiteCaucasianStudents: {
    label: 'White/Caucasian',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctBlackAfricanAmericanStudents: {
    label: 'Black/African American',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctHispanicLatinoStudents: {
    label: 'Hispanic/Latino',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctAsianStudents: {
    label: 'Asian',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctNativeAmericanStudents: {
    label: 'Native American',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
  pctOtherUndisclosedStudents: {
    label: 'Other/Undisclosed',
    range: '{min}% - {max}%',
    min: '≥{value}%',
    max: '≤{value}%',
  },
}

type ChipGenerator = (formState: UIFilters) => FilterChip | undefined

const FILTER_CHIP_GENERATORS: Record<keyof UIFilters, ChipGenerator> = {
  locations: (formState) => {
    if (formState.locations === undefined || formState.locations.length === 0) return undefined
    const locations = formState.locations.map((loc) => (loc == unknownLocation ? unknownLocationOption.name : loc))
    return {
      key: 'locations',
      label: `${locations.length > 1 ? 'States' : 'State'}: ${locations.join(', ')}`,
    }
  },
  cities: (formState) => {
    if (formState.cities === undefined || formState.cities.length === 0) return undefined
    return {
      key: 'cities',
      label: `${formState.cities.length > 1 ? 'Cities' : 'City'}: ${formState.cities.join(', ')}`,
    }
  },
  religiousAffiliation: (formState) => {
    if (formState.religiousAffiliation === undefined || formState.religiousAffiliation.length === 0) return undefined
    return {
      key: 'religiousAffiliation',
      label: `${
        formState.religiousAffiliation.length > 1 ? 'Religious Affiliations' : 'Religious Affiliation'
      }: ${formState.religiousAffiliation.map((v) => (v === '' ? 'Unknown' : v)).join(', ')}`,
    }
  },
  virtual: (formState) => {
    if (formState.virtual === undefined) return undefined
    return {
      key: 'virtual',
      label: `Visit Format: ${formState.virtual ? VIRTUAL_LABELS.virtual : VIRTUAL_LABELS.inPerson}`,
    }
  },
  collegeFair: (formState) => {
    if (formState.collegeFair === undefined) return undefined
    return {
      key: 'collegeFair',
      label: `Visit Type: ${
        formState.collegeFair ? COLLEGE_FAIR_LABELS.collegeFair : COLLEGE_FAIR_LABELS.highSchoolVisit
      }`,
    }
  },
  locale: (formState) => {
    if (formState.locale === undefined || formState.locale.length === 0) return undefined
    return {
      key: 'locale',
      label: `${formState.locale.length > 1 ? 'Locales' : 'Locale'}: ${formState.locale
        .map((v) => (v === '' ? 'Unknown' : v))
        .join(', ')}`,
    }
  },
  zipCodes: (formState) => {
    if (formState.zipCodes === undefined || formState.zipCodes.length === 0) return undefined
    return {
      key: 'zipCodes',
      label: `${formState.zipCodes.length > 1 ? 'Zip Codes' : 'Zip Code'}: ${formState.zipCodes.join(', ')}`,
    }
  },
  geomarkets: (formState) => {
    if (formState.geomarkets === undefined || formState.geomarkets.length === 0) return undefined
    return {
      key: 'geomarkets',
      label: `${formState.geomarkets.length > 1 ? 'Geomarkets' : 'Geomarket'}: ${formState.geomarkets.join(', ')}`,
    }
  },
  visitRange: (formState) => {
    if (!formState.visitRange) return undefined

    const { type, startDate, endDate } = formState.visitRange
    if (type === 'custom') {
      if (startDate && endDate) {
        return {
          key: 'visitRange',
          label: `Visits: ${FILTER_LABELS.visitRange.date.range
            .replace('{start}', format(startDate, 'MMM d'))
            .replace('{end}', format(endDate, 'MMM d, yyyy'))}`,
        }
      } else if (startDate) {
        return {
          key: 'visitRange',
          label: `Visits: ${FILTER_LABELS.visitRange.date.from} ${format(startDate, 'MMM d, yyyy')}`,
        }
      } else if (endDate) {
        return {
          key: 'visitRange',
          label: `Visits: ${FILTER_LABELS.visitRange.date.until} ${format(endDate, 'MMM d, yyyy')}`,
        }
      }
    } else if (type) {
      return {
        key: 'visitRange',
        label: `Visits: ${FILTER_LABELS.visitRange.type[type]}`,
      }
    }
    return undefined
  },

  schoolType: (formState) => {
    if (!formState.schoolType) return undefined
    return {
      key: 'schoolType',
      label: `School Type: ${FILTER_LABELS.schoolType[formState.schoolType]}`,
    }
  },

  title1Program: (formState) => {
    if (!formState.title1Program) return undefined
    const program = FILTER_LABELS.title1Program[formState.title1Program]
    if (!program) return undefined
    return {
      key: 'title1Program',
      label: `Title 1 Program: ${program.label}`,
    }
  },

  pctFreeReducedLunch: (formState) => {
    if (!formState.pctFreeReducedLunch) return undefined
    const { min, max } = formState.pctFreeReducedLunch
    const hasMin = min != undefined
    const hasMax = max != undefined
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.pctFreeReducedLunch
    if (hasMin && hasMax) {
      return {
        key: 'pctFreeReducedLunch',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (hasMin) {
      return {
        key: 'pctFreeReducedLunch',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (hasMax) {
      return {
        key: 'pctFreeReducedLunch',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  pctAttendTwoYearCollegeStudents: (formState) => {
    if (!formState.pctAttendTwoYearCollegeStudents) return undefined
    const { min, max } = formState.pctAttendTwoYearCollegeStudents
    const hasMin = min != undefined
    const hasMax = max != undefined
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.pctAttendTwoYearCollegeStudents
    if (hasMin && hasMax) {
      return {
        key: 'pctAttendTwoYearCollegeStudents',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (hasMin) {
      return {
        key: 'pctAttendTwoYearCollegeStudents',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (hasMax) {
      return {
        key: 'pctAttendTwoYearCollegeStudents',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  pctAttendFourYearCollegeStudents: (formState) => {
    if (!formState.pctAttendFourYearCollegeStudents) return undefined
    const { min, max } = formState.pctAttendFourYearCollegeStudents
    const hasMin = min != undefined
    const hasMax = max != undefined
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.pctAttendFourYearCollegeStudents
    if (hasMin && hasMax) {
      return {
        key: 'pctAttendFourYearCollegeStudents',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (hasMin) {
      return {
        key: 'pctAttendFourYearCollegeStudents',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (hasMax) {
      return {
        key: 'pctAttendFourYearCollegeStudents',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  pctFirstGenerationStudents: (formState) => {
    if (!formState.pctFirstGenerationStudents) return undefined
    const { min, max } = formState.pctFirstGenerationStudents
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.pctFirstGenerationStudents
    if (min && max) {
      return {
        key: 'pctFirstGenerationStudents',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min) {
      return {
        key: 'pctFirstGenerationStudents',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max) {
      return {
        key: 'pctFirstGenerationStudents',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  classSize: (formState) => {
    if (!formState.classSize) return undefined
    const { min, max } = formState.classSize
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.classSize
    if (min && max) {
      return {
        key: 'classSize',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min) {
      return {
        key: 'classSize',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max) {
      return {
        key: 'classSize',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  interestedHandraisers: (formState) => {
    if (!formState.interestedHandraisers) return undefined
    const { min, max } = formState.interestedHandraisers
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.interestedHandraisers
    if (min != undefined && max != undefined) {
      return {
        key: 'interestedHandraisers',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min !== undefined) {
      return {
        key: 'interestedHandraisers',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max !== undefined) {
      return {
        key: 'interestedHandraisers',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  interestedFollowers: (formState) => {
    if (!formState.interestedFollowers) return undefined
    const { min, max } = formState.interestedFollowers
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.interestedFollowers
    if (min != undefined && max != undefined) {
      return {
        key: 'interestedFollowers',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min !== undefined) {
      return {
        key: 'interestedFollowers',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max !== undefined) {
      return {
        key: 'interestedFollowers',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  historicFollowers: (formState) => {
    if (!formState.historicFollowers) return undefined
    const { min, max } = formState.historicFollowers
    const { label, range, min: minLabel, max: maxLabel } = FILTER_LABELS.historicFollowers
    if (min != undefined && max != undefined) {
      return {
        key: 'historicFollowers',
        label: `${label}: ${range.replace('{min}', min.toString()).replace('{max}', max.toString())}`,
      }
    } else if (min !== undefined) {
      return {
        key: 'historicFollowers',
        label: `${label}: ${minLabel.replace('{value}', min.toString())}`,
      }
    } else if (max !== undefined) {
      return {
        key: 'historicFollowers',
        label: `${label}: ${maxLabel.replace('{value}', max.toString())}`,
      }
    }
    return undefined
  },

  isActive: (formState) => {
    if (formState.isActive === undefined) return undefined
    return {
      key: 'isActive',
      label: `Viewing: ${FILTER_LABELS.isActive}`,
    }
  },

  isTitle1Eligible: (formState) => {
    if (formState.isTitle1Eligible === undefined) return undefined
    return {
      key: 'isTitle1Eligible',
      label: `Title 1 Eligibility: ${formState.isTitle1Eligible ? 'Eligible' : 'Not Eligible'}`,
    }
  },

  text: (formState) => {
    if (!formState.text) return undefined
    return {
      key: 'text',
      label: `Search: ${formState.text}`,
    }
  },
  pctWhiteCaucasianStudents: (formState) =>
    createRangeFilterChip(
      'pctWhiteCaucasianStudents',
      formState.pctWhiteCaucasianStudents,
      FILTER_LABELS.pctWhiteCaucasianStudents,
    ),

  pctBlackAfricanAmericanStudents: (formState) =>
    createRangeFilterChip(
      'pctBlackAfricanAmericanStudents',
      formState.pctBlackAfricanAmericanStudents,
      FILTER_LABELS.pctBlackAfricanAmericanStudents,
    ),

  pctHispanicLatinoStudents: (formState) =>
    createRangeFilterChip(
      'pctHispanicLatinoStudents',
      formState.pctHispanicLatinoStudents,
      FILTER_LABELS.pctHispanicLatinoStudents,
    ),

  pctAsianStudents: (formState) =>
    createRangeFilterChip('pctAsianStudents', formState.pctAsianStudents, FILTER_LABELS.pctAsianStudents),

  pctNativeAmericanStudents: (formState) =>
    createRangeFilterChip(
      'pctNativeAmericanStudents',
      formState.pctNativeAmericanStudents,
      FILTER_LABELS.pctNativeAmericanStudents,
    ),

  pctOtherUndisclosedStudents: (formState) =>
    createRangeFilterChip(
      'pctOtherUndisclosedStudents',
      formState.pctOtherUndisclosedStudents,
      FILTER_LABELS.pctOtherUndisclosedStudents,
    ),
  hasCounselorSuggestions: (formState) => {
    if (formState.hasCounselorSuggestions === undefined) return undefined
    return {
      key: 'hasCounselorSuggestions',
      label: `Promoter School: ${formState.hasCounselorSuggestions ? 'Yes' : 'No'}`,
    }
  },
  isTarget: (formState) => {
    if (formState.isTarget === undefined) return undefined
    return {
      key: 'isTarget',
      label: `Target High School: ${formState.isTarget ? 'Yes' : 'No'}`,
    }
  },
}

function getFilterChips(formState: UIFilters, onRemove: (key: keyof UIFilters) => void) {
  const chips: FilterChip[] = []
  Object.entries(FILTER_CHIP_GENERATORS).forEach(([, generator]) => {
    const chip = generator(formState)
    if (chip) {
      chips.push(chip)
    }
  })

  return chips.map((chip) => (
    <Chip
      key={chip.key}
      label={chip.label}
      onDelete={() => onRemove(chip.key)}
      color="primary"
      variant="outlined"
      size="small"
    />
  ))
}

const useStyles = makeStyles((theme: Theme) => ({
  appliedFilterSection: {
    backgroundColor: theme.palette.pageBackground?.main,
  },
}))

export default function FilterDrawer({
  isOpen,
  onClose,
  onClickApply,
  defaultFilters,
  showPremiumFilters,
  selectedHandraiserClasses,
  selectedFollowerClasses,
  selectedHistoricFollowerYears,
}: {
  isOpen: boolean
  onClose: () => void
  onClickApply: (f: UIFilters) => void
  defaultFilters: UIFilters
  showPremiumFilters: boolean
  selectedHandraiserClasses?: Class[]
  selectedFollowerClasses?: Class[]
  selectedHistoricFollowerYears?: HistoricFollowerYear[]
}) {
  const classes = useStyles()
  const [state, dispatch] = useReducer(reducer, {
    formState: {
      ...defaultFilters,
    },
  })

  useEffect(() => {
    dispatch({
      type: 'replace-form-state',
      formState: defaultFilters,
    })
  }, [defaultFilters])

  const count = getFilterCount(state.formState)

  const handleRemoveFilter = (key: keyof UIFilters) => {
    dispatch({
      type: 'update-form-state',
      formState: { [key]: undefined },
    })
  }

  const errors = validate(state.formState)

  const handleApply = () => {
    onClickApply(state.formState)
  }

  return (
    <Drawer
      anchor="left"
      open={isOpen}
      onClose={onClose}
      maxWidth={460}
      heading={
        <Heading
          variant="h6"
          component="h3"
        >
          Filters
        </Heading>
      }
      bodyPadding={false}
      footer={
        <Box
          padding={2}
          display="flex"
          justifyContent="space-between"
        >
          <Button
            variant="text"
            size="large"
            color="primary"
            onClick={() => {
              dispatch({ type: 'reset-form-state' })
            }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApply}
            disabled={Object.keys(errors).length > 0}
          >
            Apply Filters
          </Button>
        </Box>
      }
    >
      <Box>
        {count > 0 && (
          <SectionBox className={classes.appliedFilterSection}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                component="div"
                variant="h6"
              >
                Applied Filters
              </Typography>
              <Button
                variant="text"
                size="large"
                color="primary"
                onClick={() => {
                  dispatch({ type: 'reset-form-state' })
                }}
              >
                Clear All
              </Button>
            </Box>
            <Box
              mt={2}
              display="flex"
              flexWrap="wrap"
              style={{ gap: 8 }}
            >
              {getFilterChips(state.formState, handleRemoveFilter)}
            </Box>
          </SectionBox>
        )}
        <SectionBox>
          <FilterVisits
            visitRange={state.formState.visitRange}
            virtual={state.formState.virtual}
            collegeFair={state.formState.collegeFair}
            onChangeVisit={(option?: string) => {
              dispatch({
                type: 'select-visit-availability-option',
                option: option as AvailabilityOption,
              })
            }}
            onChangeVisitDates={(dates) => {
              dispatch({
                type: 'update-visibility-dates',
                visitAvailabilityDates: {
                  startDate: dates.startDate,
                  endDate: dates.endDate,
                },
              })
            }}
            onChangeType={(formType: 'virtual' | 'collegeFair', value?: boolean) => {
              dispatch({
                type: 'update-form-state',
                formState: { [formType]: value },
              })
            }}
          />
        </SectionBox>
        <SectionBox>
          <FilterSchoolDetails
            schoolType={state.formState.schoolType}
            classSize={state.formState.classSize}
            religiousAffiliation={state.formState.religiousAffiliation}
            isActive={state.formState.isActive}
            isTitle1Eligible={state.formState.isTitle1Eligible}
            title1Program={state.formState.title1Program}
            pctFreeReducedLunch={state.formState.pctFreeReducedLunch}
            onChange={(formState) => {
              dispatch({
                type: 'update-form-state',
                formState,
              })
            }}
            errors={errors}
            enabled={showPremiumFilters}
            isTarget={state.formState.isTarget}
          />
        </SectionBox>
        <SectionBox>
          <FilterLocations
            selectedLocations={state.formState.locations}
            onChangeLocation={(locations) => {
              dispatch({
                type: 'update-form-state',
                formState: { locations },
              })
            }}
            selectedCities={state.formState.cities || []}
            onChangeCities={(cities) => {
              dispatch({
                type: 'update-form-state',
                formState: { cities: cities.length > 0 ? cities : undefined },
              })
            }}
            selectedZipCodes={state.formState.zipCodes || []}
            onChangeZipCodes={(zipCodes: string[]) => {
              dispatch({
                type: 'update-form-state',
                formState: { zipCodes: zipCodes.length > 0 ? zipCodes : undefined },
              })
            }}
            selectedGeomarkets={state.formState.geomarkets || []}
            onChangeGeomarkets={(geomarkets: string[]) => {
              dispatch({
                type: 'update-form-state',
                formState: { geomarkets: geomarkets.length > 0 ? geomarkets : undefined },
              })
            }}
            enabled={showPremiumFilters}
            locale={state.formState.locale || []}
            setLocale={(locale: LocaleOption[]) => {
              dispatch({
                type: 'update-form-state',
                formState: { locale: locale.length > 0 ? locale : undefined },
              })
            }}
          />
        </SectionBox>
        <SectionBox>
          <FilterDemographics
            pctFirstGenerationStudents={state.formState.pctFirstGenerationStudents}
            pctWhiteCaucasianStudents={state.formState.pctWhiteCaucasianStudents}
            pctBlackAfricanAmericanStudents={state.formState.pctBlackAfricanAmericanStudents}
            pctHispanicLatinoStudents={state.formState.pctHispanicLatinoStudents}
            pctNativeAmericanStudents={state.formState.pctNativeAmericanStudents}
            pctAsianStudents={state.formState.pctAsianStudents}
            pctOtherUndisclosedStudents={state.formState.pctOtherUndisclosedStudents}
            onChange={(formState) => {
              dispatch({
                type: 'update-form-state',
                formState,
              })
            }}
            enabled={showPremiumFilters}
          />
        </SectionBox>
        <SectionBox>
          <FilterStudentEngagement
            interestedHandraisers={state.formState.interestedHandraisers}
            interestedFollowers={state.formState.interestedFollowers}
            historicFollowers={state.formState.historicFollowers}
            hasCounselorSuggestions={state.formState.hasCounselorSuggestions}
            selectedHandraiserClasses={selectedHandraiserClasses}
            selectedFollowerClasses={selectedFollowerClasses}
            selectedHistoricFollowerYears={selectedHistoricFollowerYears}
            onChange={(formState) => {
              dispatch({
                type: 'update-form-state',
                formState,
              })
            }}
            errors={errors}
            enabled={showPremiumFilters}
          />
        </SectionBox>
        <SectionBox>
          <FilterPostSecondary
            pctAttendFourYearCollegeStudents={state.formState.pctAttendFourYearCollegeStudents}
            pctAttendTwoYearCollegeStudents={state.formState.pctAttendTwoYearCollegeStudents}
            onChange={(formState) => {
              dispatch({
                type: 'update-form-state',
                formState,
              })
            }}
            enabled={showPremiumFilters}
          />
        </SectionBox>
      </Box>
    </Drawer>
  )
}

export const numericValueOrDefault = (v?: number, def: string = ''): number | string => {
  return v === undefined ? def : v
}

type ValidationResult = {
  [K in keyof UIFilters]: {
    helperText: string
  }
}

function validate(state: UIFilters): Partial<ValidationResult> {
  if (state.pctAttendFourYearCollegeStudents !== undefined) {
    if (state.pctAttendFourYearCollegeStudents.min !== undefined && state.pctAttendFourYearCollegeStudents.min < 0) {
      return {
        pctAttendFourYearCollegeStudents: {
          helperText: 'Minimum must be greater or equal to 0',
        },
      }
    }

    if (state.pctAttendFourYearCollegeStudents.max !== undefined && state.pctAttendFourYearCollegeStudents.max < 1) {
      return {
        pctAttendFourYearCollegeStudents: {
          helperText: 'Maximum must be greater than 0',
        },
      }
    }

    if (
      state.pctAttendFourYearCollegeStudents.min !== undefined &&
      state.pctAttendFourYearCollegeStudents.max !== undefined &&
      state.pctAttendFourYearCollegeStudents.min >= state.pctAttendFourYearCollegeStudents.max
    ) {
      return {
        pctAttendFourYearCollegeStudents: {
          helperText: 'Maximum must be greater than minimum',
        },
      }
    }
  }

  if (state.pctAttendTwoYearCollegeStudents !== undefined) {
    if (state.pctAttendTwoYearCollegeStudents.min !== undefined && state.pctAttendTwoYearCollegeStudents.min < 0) {
      return {
        pctAttendTwoYearCollegeStudents: {
          helperText: 'Minimum must be greater or equal to 0',
        },
      }
    }

    if (state.pctAttendTwoYearCollegeStudents.max !== undefined && state.pctAttendTwoYearCollegeStudents.max < 1) {
      return {
        pctAttendTwoYearCollegeStudents: {
          helperText: 'Maximum must be greater than 0',
        },
      }
    }

    if (
      state.pctAttendTwoYearCollegeStudents.min !== undefined &&
      state.pctAttendTwoYearCollegeStudents.max !== undefined &&
      state.pctAttendTwoYearCollegeStudents.min >= state.pctAttendTwoYearCollegeStudents.max
    ) {
      return {
        pctAttendTwoYearCollegeStudents: {
          helperText: 'Maximum must be greater than minimum',
        },
      }
    }
  }

  if (state.classSize !== undefined) {
    if (state.classSize?.max !== undefined && state.classSize?.max < 1) {
      return {
        classSize: {
          helperText: 'Maximum must be greater than 0',
        },
      }
    }

    if (
      state.classSize?.min !== undefined &&
      state.classSize?.max !== undefined &&
      state.classSize?.min >= state.classSize?.max
    ) {
      return {
        classSize: {
          helperText: 'Maximum must be greater than minimum',
        },
      }
    }
  }

  if (state.interestedHandraisers !== undefined) {
    if (state.interestedHandraisers?.min !== undefined && state.interestedHandraisers?.min < 0) {
      return {
        interestedHandraisers: {
          helperText: 'Minimum must be greater or equal to 0',
        },
      }
    }

    if (state.interestedHandraisers?.max !== undefined && state.interestedHandraisers?.max < 0) {
      return {
        interestedHandraisers: {
          helperText: 'Maximum must be greater or equal to 0',
        },
      }
    }

    if (
      state.interestedHandraisers?.min !== undefined &&
      state.interestedHandraisers?.max !== undefined &&
      state.interestedHandraisers?.min >= state.interestedHandraisers?.max
    ) {
      return {
        interestedHandraisers: {
          helperText: 'Maximum must be greater than minimum',
        },
      }
    }
  }

  if (state.interestedFollowers !== undefined) {
    if (state.interestedFollowers?.min !== undefined && state.interestedFollowers?.min < 0) {
      return {
        interestedFollowers: {
          helperText: 'Minimum must be greater or equal to 0',
        },
      }
    }

    if (state.interestedFollowers?.max !== undefined && state.interestedFollowers?.max < 0) {
      return {
        interestedFollowers: {
          helperText: 'Maximum must be greater or equal to 0',
        },
      }
    }

    if (
      state.interestedFollowers?.min !== undefined &&
      state.interestedFollowers?.max !== undefined &&
      state.interestedFollowers?.min >= state.interestedFollowers?.max
    ) {
      return {
        interestedFollowers: {
          helperText: 'Maximum must be greater than minimum',
        },
      }
    }
  }

  if (state.historicFollowers !== undefined) {
    if (state.historicFollowers?.min !== undefined && state.historicFollowers?.min < 0) {
      return {
        historicFollowers: {
          helperText: 'Minimum must be greater or equal to 0',
        },
      }
    }

    if (state.historicFollowers?.max !== undefined && state.historicFollowers?.max < 0) {
      return {
        historicFollowers: {
          helperText: 'Maximum must be greater or equal to 0',
        },
      }
    }

    if (
      state.historicFollowers?.min !== undefined &&
      state.historicFollowers?.max !== undefined &&
      state.historicFollowers?.min >= state.historicFollowers?.max
    ) {
      return {
        historicFollowers: {
          helperText: 'Maximum must be greater than minimum',
        },
      }
    }
  }

  if (
    state.visitRange?.startDate &&
    state.visitRange?.endDate &&
    isAfter(state.visitRange?.startDate, state.visitRange?.endDate)
  ) {
    return {
      visitRange: {
        helperText: 'Visits To must be after From',
      },
    }
  }
  if (
    state.visitRange?.type === 'custom' &&
    state.visitRange?.startDate === undefined &&
    state.visitRange?.endDate === undefined
  ) {
    return {
      visitRange: {
        helperText: 'Please select a date range',
      },
    }
  }

  return {}
}

export type AvailabilityOption = NonNullable<UIFilters['visitRange']>['type']

function getVisitVailabilityFromOption(option: AvailabilityOption | undefined): UIFilters['visitRange'] {
  const now = new Date()
  switch (option) {
    case 'this-week': {
      const startDate = startOfWeek(now)
      const endDate = endOfWeek(now)
      return { type: option, startDate, endDate }
    }
    case 'this-month': {
      const startDate = startOfMonth(now)
      const endDate = endOfMonth(now)
      return { type: option, startDate, endDate }
    }
    case 'next-month': {
      const nextMonth = addMonths(now, 1)
      const startDate = startOfMonth(nextMonth)
      const endDate = endOfMonth(nextMonth)
      return { type: option, startDate, endDate }
    }
    case 'custom':
      return { type: option }
    default:
      return undefined
  }
}
const dateOrFallback = (date: Date | null | undefined, fallback: Date | undefined): Date | undefined => {
  if (date === null) return undefined
  return date || fallback
}
